/**
 * design_handoff_auth §3/§3b/§3.17 — create-account form. Provider button
 * first, then the `or` rule, then the email form (FIXES.md #16). Name and
 * Workspace are derived from the email and re-derive on every keystroke
 * until the user edits either field themselves (FIXES.md #17). Captcha
 * sits directly above the submit button; no per-field password hint.
 *
 * Also used, with different props, for design_handoff_accept_invitation §4
 * screen 2: heading/subtitle overridden, Workspace hidden (there's no
 * org to create, they're joining one), Email read-only with a hint.
 * Created by piotr.pozniak@thebeaverhead.com on 21/11/2022
 *
 * @param {Object=} formValues
 * @param {boolean=true} displayOrganizationName - false hides the Workspace field entirely (invite flow: joining, not creating)
 * @param {boolean=true} displayCouponLink
 * @param {boolean=false} emailReadOnly - invite flow: the address is fixed to the invitation
 * @param {string=} emailHint
 * @param {ReactNode=} heading
 * @param {ReactNode=} subtitle
 * @param {string=} submitBtnText
 * @param {function=} onRegisterSuccess - called instead of the default /dashboard redirect on a verified account
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { store } from "./../../store";
import isEmail from "validator/lib/isEmail";
import { useUserStore } from "./../../../../hooks/redux/user";
import { useAppStore } from "./../../../../hooks/redux/app";
import useAuthHook from "./../../hooks/useAuthHook";
import useRedirectUrl from "../../hooks/useRedirectUrl";
import ReCAPTCHA from "react-google-recaptcha";
import { Input, Button } from "../../theme/untitled";
import GoogleButton from "../AuthPages/GoogleButton";
import AuthAlert from "../AuthPages/AuthAlert";
import appConfig from "../../../../appConfig";
import { formatError } from "../../../../helpers/errors";
import styles from "../AuthPages/authForm.module.scss";

const MIN_PASSWORD_LENGTH = 6;

const splitName = (name) => {
  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

// FIXES.md #17: localpart.split(/[._-]/).filter(t => !/^\d+$/.test(t)).map(titleCase).join(' ')
const deriveNameFromEmail = (email) => {
  const localPart = (email.split("@")[0] || "").trim();
  const tokens = localPart.split(/[._-]/).filter((t) => t && !/^\d+$/.test(t));
  return tokens
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
    .join(" ");
};

const EmailSignUpForm = (props) => {
  const displayOrganizationName = props.displayOrganizationName ?? true;
  const displayCouponLink = props.displayCouponLink ?? true;

  // Lazy init: when the email arrives pre-filled and read-only (invite
  // flow), onEmailChange never fires to derive the Name, since there's no
  // change event on a field the user can't edit. Derive it once up front.
  const [state, setState] = useState(() => {
    const initialEmail = (props.formValues && props.formValues.email) ?? "";
    return {
      name: props.emailReadOnly ? deriveNameFromEmail(initialEmail) : "",
      organizationName:
        (props.formValues && props.formValues.organizationName) ?? "",
      email: initialEmail,
      password: "",
      captchaToken: "",
      coupon: "",
      organization_uuid:
        (props.formValues && props.formValues.organization_uuid) ?? null,

      nameError: null,
      organizationNameError: null,
      emailError: null,
      passwordError: null,
      captchaError: null,

      hasCoupon: false,
    };
  });

  const [nameEdited, setNameEdited] = useState(false);
  const [workspaceEdited, setWorkspaceEdited] = useState(false);

  const { clearErrors } = useAppStore();
  const { user, register, setPluginLinkRedirect } = useUserStore();
  const { isLoading: authLoading, onGoogleSignUp } = useAuthHook();
  const captchaRef = useRef(null);
  const { redirectOrNavigateTo, redirectUrl, createUrlWithRedirect } =
    useRedirectUrl();

  useEffect(() => {
    return clearErrors;
  }, []);

  useEffect(() => {
    if (state.hasCoupon) {
      document.getElementById("invite_code")?.focus();
    }
  }, [state.hasCoupon]);

  useEffect(() => {
    if (user.registerError && captchaRef.current) {
      captchaRef.current.reset();
    }
  }, [user.registerError]);

  const onCaptchaTokenSet = (token) => {
    setState((prev) => ({ ...prev, captchaToken: token, captchaError: null }));
  };

  const onRegisterClick = async (e) => {
    e.preventDefault();

    const {
      name,
      email,
      password,
      coupon,
      organizationName,
      organization_uuid,
    } = state;
    const { firstName, lastName } = splitName(name);

    let emailError = null;
    let passwordError = null;
    let nameError = null;
    let captchaError = null;

    if (!isEmail(email)) {
      emailError = "Please enter a valid email address.";
    }

    if (!firstName || firstName.length < 2) {
      nameError = "Please enter your name.";
    }

    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      passwordError = `Please enter a password, at least ${MIN_PASSWORD_LENGTH} characters.`;
    }

    if (!state.captchaToken) {
      captchaError = "Confirm you are not a robot.";
    }

    setState((prev) => ({
      ...prev,
      nameError,
      passwordError,
      emailError,
      captchaError,
    }));

    if (!nameError && !passwordError && !emailError && !captchaError) {
      await register({
        first_name: firstName,
        last_name: lastName,
        // Only meaningful when organization_uuid is empty (creating a new
        // org); the invite flow joins an existing one and hides this field.
        organization_name: displayOrganizationName
          ? organizationName
          : undefined,
        email,
        password,
        trial_coupon: coupon.toUpperCase(),
        captcha_token: state.captchaToken,
        organization_uuid,
      });

      if (store.getState().user.registerSuccess) {
        if (store.getState().user.model.is_account_verified) {
          if (props.onRegisterSuccess) {
            props.onRegisterSuccess();
          } else {
            redirectOrNavigateTo("/dashboard");
          }
        } else {
          redirectOrNavigateTo("/verify-email");
        }
      }
    }

    return false;
  };

  const onHaveInvitationCode = (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, hasCoupon: true }));
  };

  const isLoading = user.register || authLoading;

  const onEmailChange = useCallback(
    (e) => {
      const email = e.target.value;
      setState((prev) => {
        const next = { ...prev, email };
        if (!nameEdited || (displayOrganizationName && !workspaceEdited)) {
          const derivedName = deriveNameFromEmail(email);
          if (!nameEdited) next.name = derivedName;
          if (displayOrganizationName && !workspaceEdited) {
            next.organizationName = derivedName
              ? `${derivedName}'s workspace`
              : "";
          }
        }
        return next;
      });
    },
    [nameEdited, workspaceEdited, displayOrganizationName]
  );

  const onNameChange = useCallback((e) => {
    setNameEdited(true);
    setState((prev) => ({ ...prev, name: e.target.value }));
  }, []);

  const onWorkspaceChange = useCallback((e) => {
    setWorkspaceEdited(true);
    setState((prev) => ({ ...prev, organizationName: e.target.value }));
  }, []);

  const onPasswordChange = useCallback((e) => {
    setState((prev) => ({ ...prev, password: e.target.value }));
  }, []);

  const onCouponChange = useCallback((e) => {
    setState((prev) => ({ ...prev, coupon: e.target.value }));
  }, []);

  const onSignupWithGoogleClick = useCallback(
    (e) => {
      if (isLoading) {
        return;
      }
      if (redirectUrl) {
        // store the redirect url so, once the user comes back from Google
        // oAuth, they follow the registration flow and land back on it.
        setPluginLinkRedirect(redirectUrl);
      }
      onGoogleSignUp(
        "register" +
          (props.formValues && props.formValues.invitationHash
            ? "&invitationHash=" + props.formValues.invitationHash
            : "")
      )(e);
    },
    [isLoading, redirectUrl]
  );

  const submitBtnText = props.submitBtnText || "Create free account";

  const apiErrorMessage = useMemo(() => {
    if (!user.registerError) {
      return null;
    }
    return formatError(
      user.registerError,
      "Could not create the account. Please try again."
    ).join(" ");
  }, [user.registerError]);

  return (
    <form action="/" noValidate>
      <h1 className={styles.title}>
        {props.heading || "Create your free account"}
      </h1>
      {props.subtitle || (
        <p className={styles.sub}>
          Free forever on the starter quotas.
          <br />
          Your first widget is live in about two minutes.
        </p>
      )}

      <div className={styles.fields}>
        <GoogleButton
          label="Sign up with Google"
          disabled={isLoading}
          onClick={onSignupWithGoogleClick}
        />

        <div className={styles.divider}>or</div>

        <Input
          autoFocus={!props.emailReadOnly}
          label="Email"
          type="email"
          value={state.email}
          onChange={onEmailChange}
          disabled={isLoading}
          readOnly={props.emailReadOnly}
          hint={props.emailHint}
          error={state.emailError}
        />

        <Input
          autoFocus={props.emailReadOnly}
          label="Name"
          type="text"
          value={state.name}
          onChange={onNameChange}
          disabled={isLoading}
          error={state.nameError}
        />

        {displayOrganizationName && (
          <Input
            label="Workspace"
            type="text"
            value={state.organizationName}
            onChange={onWorkspaceChange}
            disabled={isLoading}
            error={state.organizationNameError}
          />
        )}

        <Input
          label="Password"
          type="password"
          placeholder=""
          value={state.password}
          onChange={onPasswordChange}
          disabled={isLoading}
          error={state.passwordError}
        />

        {!state.hasCoupon && displayCouponLink && (
          <p className={styles.inviteCodePrompt}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={onHaveInvitationCode}
            >
              Do you have an invite code?
            </button>
          </p>
        )}

        {state.hasCoupon && (
          <Input
            id="invite_code"
            label="Invite code (optional)"
            type="text"
            value={state.coupon}
            onChange={onCouponChange}
            disabled={isLoading}
          />
        )}

        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.RECAPTCHA_SITE_KEY}
          onChange={onCaptchaTokenSet}
        />
        {state.captchaError && (
          <span className={styles.hint}>{state.captchaError}</span>
        )}

        {apiErrorMessage && (
          <AuthAlert variant="error">{apiErrorMessage}</AuthAlert>
        )}

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          onClick={onRegisterClick}
        >
          {submitBtnText}
        </Button>

        <p className={styles.hint}>
          By continuing you agree to the{" "}
          <a href={appConfig.tos_link} target="_app-tos">
            Terms
          </a>{" "}
          and{" "}
          <a href={appConfig.pp_link} target="_app-pp">
            Privacy Policy
          </a>
          .
        </p>
      </div>

      <p className={styles.footQuestion}>
        Already have an account?
        <Link to={createUrlWithRedirect("/login")}>Log in</Link>
      </p>
    </form>
  );
};

export default EmailSignUpForm;
